/**
 * The wiring behind „Przypisz ankietę": the templates read, the ticks, and one write per ticked
 * check-in, sent in parallel.
 *
 * Not optimistic, as the matrix's cell is not (`pages/reports/lib/useMatrixTab`): the button goes
 * busy, and the dialog closes only once the refetched lists say the client has every form. A partial
 * failure keeps the dialog open: the refetch turns what went into „Przypisana", what did not stays
 * ticked, and the same button sends it again.
 *
 * The caller names the reads the write changes beyond the templates list (`invalidates`) — the
 * client's survey history, which this feature may not import — exactly as `features/survey-review`
 * takes its own.
 */

import { useState } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  surveyGroups,
  surveyTemplatesQueryKey,
  surveyTemplatesQueryOptions,
} from '~/entities/survey-template';
import { usePostSurveyTemplateIdAssignments } from '~/shared/api/generated/endpoints/survey/survey';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';

import {
  assignOutcome,
  sendableSurveys,
  surveyChoiceRows,
  toggleSurvey,
  type AssignOutcome,
  type SurveyChoiceRow,
} from '../model/surveyChoices';

export interface AssignSurveyState {
  /** What the list draws — the gate's wait first, then the read's own answer. */
  readonly content: 'waiting' | 'failed' | 'empty' | 'ready';
  readonly gate: SkeletonGate;
  readonly rows: readonly SurveyChoiceRow[];
  readonly selected: ReadonlySet<number>;
  readonly toggle: (id: number) => void;
  /** How many ticked check-ins „Przypisz" would send. */
  readonly count: number;
  readonly assigning: boolean;
  /** The last send's answer while some of it failed; `null` otherwise. */
  readonly failure: AssignOutcome | null;
  /** Send the ticked check-ins. `onAssigned` runs once every write and refetch landed, with the titles. */
  readonly assign: (onAssigned: (surveyTitles: readonly string[]) => void) => void;
  readonly retry: () => void;
}

export function useAssignSurvey(
  clientId: number,
  invalidates: readonly QueryKey[],
): AssignSurveyState {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const templates = useQuery(surveyTemplatesQueryOptions());
  const write = usePostSurveyTemplateIdAssignments();
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [assigning, setAssigning] = useState(false);
  const [failure, setFailure] = useState<AssignOutcome | null>(null);

  const rows = surveyChoiceRows(surveyGroups(templates.data, i18n.language), clientId, t);
  const gate = useSkeletonGate(templates.isPending);
  const content = gate.waiting
    ? 'waiting'
    : templates.isError
      ? 'failed'
      : rows.length === 0
        ? 'empty'
        : 'ready';
  const sendable = sendableSurveys(selected, rows);

  return {
    content,
    gate,
    rows,
    selected,
    toggle: (id) => {
      if (assigning) return;
      setFailure(null);
      setSelected(toggleSurvey(selected, id, rows));
    },
    count: sendable.length,
    assigning,
    failure,
    assign: (onAssigned) => {
      if (assigning || sendable.length === 0) return;
      setFailure(null);
      setAssigning(true);
      void Promise.all(
        sendable.map((row) =>
          write
            .mutateAsync({ id: row.id, data: { customerIds: [clientId] } })
            .then(
              () => ({ title: row.title, ok: true }),
              () => ({ title: row.title, ok: false }),
            ),
        ),
      ).then(async (results) => {
        // The templates list carries `assignedCustomerIds`; the client's history gains the
        // occurrence the backend opens at once (R22). Refetched once, after every write answered.
        await Promise.all(
          [surveyTemplatesQueryKey, ...invalidates].map((queryKey) =>
            queryClient.invalidateQueries({ queryKey }),
          ),
        ).catch(() => undefined);
        const outcome = assignOutcome(results);
        setAssigning(false);
        if (outcome.kind === 'all') onAssigned(outcome.assigned);
        else setFailure(outcome);
      });
    },
    retry: () => {
      void templates.refetch();
    },
  };
}
